import { Fragment, useEffect } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Form, InputNumber, Space } from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { requiredRule } from 'app/common/form-helpers/features/rules';
import { NotificationsRender } from 'app/common/notifications/features/notifications';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { CanAccess } from 'app/railways/domains/access/features/permissions';
import { Loading, PageContent, Stack } from 'app/ui';
import { CardTabs } from 'app/ui/card-tabs/card-tabs';

import { TRoute } from '../../core/route';
import {
  TRouteRefund,
  TUpdatedRouteRefund,
} from '../../core/route-refund-rule';
import { getRouteRefundRequest } from '../../requests/get-route-refund.request';
import { updateRouteRefundRequest } from '../../requests/update-route-refund.request';
import { ZoneStationGrid } from '../zone-stations-grid/zone-stations-grid';

type DetailsRouteProps = {
  routeId: number;
};
export const DetailsRoute = observer((props: DetailsRouteProps) => {
  const pageNotify = usePageNotify();
  const [form] = Form.useForm();

  const getRouteResource = useResource<TRoute['id'], TRouteRefund>({
    request: getRouteRefundRequest,
    errorNotify: pageNotify,
    onDone: (routeRefund) => {
      form.setFieldsValue(routeRefund);
    },
  });

  const updateRouteRefundResource = useResource<
    TUpdatedRouteRefund,
    TRouteRefund
  >({
    request: updateRouteRefundRequest,
    errorNotify: pageNotify,
    onDone: (routeRefund) => {
      form.setFieldsValue(routeRefund);
      pageNotify.addMessage({
        type: 'success',
        message: 'Данные успешно обновлены',
      });
    },
  });

  const initialValues: TRouteRefund = {
    route: {
      id: props.routeId,
      nameRu: '',
      nameEn: '',
      nameHy: '',
      number: '',
      stationFromId: 0,
      stationToId: 0,
    },
    commissionRules: [],
  };

  const handleSubmit = (data: Omit<TUpdatedRouteRefund, 'id'>) => {
    const updatedData: TUpdatedRouteRefund = {
      route: { id: props.routeId },
      commissionRules: data.commissionRules.map((rule) => {
        const updatedRule = { ...rule };
        delete updatedRule.fromHourBeforeTrip; // legacy field
        delete updatedRule.toHourBeforeTrip; // legacy field

        return updatedRule;
      }),
    };

    updateRouteRefundResource.send(updatedData);
  };

  useEffect(() => {
    getRouteResource.send(props.routeId);
  }, []);

  const isLoading =
    getRouteResource.isLoading || updateRouteRefundResource.isLoading;

  const CommissionRefund = (
    <CanAccess perm="PERM_ROUTE_REFUND_RULE_READ">
      <Form.List name="commissionRules">
        {(fields, { add, remove }) => (
          <Fragment key="1">
            {fields.map(({ key, name, ...restField }) => (
              <Space
                key={key}
                size={20}
                style={{ display: 'flex', marginBottom: 8 }}
                align="end"
              >
                <Form.Item
                  {...restField}
                  name={[name, 'fromMinBeforeTrip']}
                  label="Начальное время до отправления"
                >
                  <InputNumber style={{ width: '100%' }} addonAfter="мин" />
                </Form.Item>
                <Form.Item
                  {...restField}
                  name={[name, 'toMinBeforeTrip']}
                  label="Конечное время до отправления"
                >
                  <InputNumber style={{ width: '100%' }} addonAfter="мин" />
                </Form.Item>
                <Form.Item
                  {...restField}
                  name={[name, 'amount']}
                  label="Значение комиссии"
                  rules={[requiredRule()]}
                >
                  <InputNumber style={{ width: '100%' }} addonAfter="֏" />
                </Form.Item>
                <Form.Item label="">
                  <Button
                    onClick={() => remove(name)}
                    icon={<DeleteOutlined />}
                    danger
                  >
                    Удалить
                  </Button>
                </Form.Item>
              </Space>
            ))}
            <CanAccess perm="PERM_ROUTE_REFUND_RULE_CREATE">
              <Form.Item key="2">
                <Button
                  type="primary"
                  onClick={() => add()}
                  block
                  ghost
                  icon={<PlusOutlined />}
                >
                  Добавить правило
                </Button>
              </Form.Item>
            </CanAccess>
          </Fragment>
        )}
      </Form.List>

      <CanAccess perm="PERM_ROUTE_REFUND_RULE_UPDATE">
        <div style={{ display: 'flex' }}>
          <Button
            htmlType="submit"
            type="primary"
            style={{ marginLeft: 'auto' }}
          >
            Сохранить
          </Button>
        </div>
      </CanAccess>
    </CanAccess>
  );

  const ContentTabItems = [
    {
      label: 'Комиссии на возврат',
      key: 'commissions',

      children: CommissionRefund,
    },
    {
      label: 'Станции и зоны',
      key: 'stations',
      children: <ZoneStationGrid routeId={props.routeId} />,
    },
  ];

  return (
    <Loading active={isLoading}>
      <PageContent>
        <Form
          layout="vertical"
          initialValues={initialValues}
          form={form}
          onFinish={handleSubmit}
        >
          <Stack>
            <Card title="Данные маршрута" size="small">
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item
                  label="ID маршрута"
                  labelStyle={{ width: '1%', whiteSpace: 'nowrap' }}
                >
                  {props.routeId}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Название маршрута"
                  labelStyle={{ width: '1%', whiteSpace: 'nowrap' }}
                >
                  {form.getFieldValue('route')?.nameRu || ''}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Номер маршрута"
                  labelStyle={{ width: '1%', whiteSpace: 'nowrap' }}
                >
                  {form.getFieldValue('route')?.number || ''}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <CardTabs items={ContentTabItems} />

            <NotificationsRender notifyStore={pageNotify} />

            {/* <Card size="small" bodyStyle={{ display: 'flex' }}>

            </Card> */}
          </Stack>
        </Form>
      </PageContent>
    </Loading>
  );
});
